import React from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import Lottie from 'react-lottie-player';
import { Redirect, useParams } from 'react-router-dom';
import {
    BodyTextMedium,
    Button,
    HeadingText50,
    ComponentTextSmall,
    Icon,
    IconButton,
    pxToRem,
} from '@modernhealth/vital-components';
import { colors } from '@modernhealth/vital-foundation';

import successAnimation from 'src/public/animations/colorful_confetti.json';
import { useQueryParams } from 'src/shared/hooks/useQueryParams';

import { usePathwaysNavigation } from 'src/modules/pathways/hooks/navigation';
import tada from 'src/public/img/tada.svg';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';
import { createRenderSwitch } from 'src/shared/components/RenderSwitch';
import { getSessionIndexFromQueryParam } from 'src/modules/pathways/helpers/getSessionIndexFromQueryParam';
import { PageContentContainer } from 'src/modules/pathways/components/PageLayout';

import { usePathwayQuery } from '../documents/pathway.generated';
import { PathwayCloudBackground } from '../components/PathwayCloudBackground';

enum SessionCompleteState {
    PATHWAY_COMPLETE,
    SESSION_COMPLETE,
}

enum SessionStatusState {
    COMPLETED,
    NEXT,
    LOCKED,
}

const RenderSwitch = createRenderSwitch<SessionStatusState>();

export const PathwaySessionCompletePage = () => {
    const queryParams = useQueryParams();
    const { locationDescriptor, navigate } = usePathwaysNavigation();
    const { t } = useTranslation();
    const { uuid } = useParams<{ uuid: string }>();
    const { data, loading } = usePathwayQuery({
        variables: {
            uuid,
        },
    });

    if (!data?.pathway || loading) {
        return <CenteredSpinner />;
    }

    const { pathway } = data;
    const { memberPathway } = pathway;

    if (!memberPathway) {
        return <Redirect to={locationDescriptor.pathways()} />;
    }

    const activeSessionIndex = getSessionIndexFromQueryParam({
        activeSessionParam: queryParams.get('activeSession'),
        sessionsLength: pathway.sessionCount,
        defaultIndex: memberPathway.currentSessionIndex,
    });
    const activeSession = activeSessionIndex + 1;

    const completeState =
        pathway.sessionCount === activeSession
            ? SessionCompleteState.PATHWAY_COMPLETE
            : SessionCompleteState.SESSION_COMPLETE;

    const sessionProgress = Array.from({ length: pathway.sessionCount }).map((_, i) => {
        const sessionNumber = i + 1;

        if (sessionNumber === activeSession + 1) {
            return SessionStatusState.NEXT;
        }

        if (sessionNumber <= activeSession) {
            return SessionStatusState.COMPLETED;
        }

        return SessionStatusState.LOCKED;
    });

    const SessionCompleteStateToProps: Record<
        SessionCompleteState,
        {
            title: string;
            description: string;
            renderImage: () => React.ReactNode;
            primaryButtonText: string;
            primaryButtonAction: () => void;
            secondaryButtonText?: string | undefined;
            secondaryButtonAction?: () => void | undefined;
        }
    > = {
        [SessionCompleteState.PATHWAY_COMPLETE]: {
            title: t('pathways:pathway_session_complete.pathway_complete.title'),
            description: t('pathways:pathway_session_complete.pathway_complete.description'),
            renderImage: () => {
                if (!memberPathway.pathwayProvider.provider.avatar) return null;

                return (
                    <ConfettiContainer>
                        <SuccessAnimation animationData={successAnimation} speed={0.6} play />
                        <ProviderAvatar
                            src={memberPathway.pathwayProvider.provider.avatar}
                            alt={t(
                                'pathways:components.pathway_provider_card.your_pathway_provider',
                            )}
                        />
                    </ConfettiContainer>
                );
            },
            primaryButtonText: t(
                'pathways:pathway_session_complete.pathway_complete.action.share_feedback',
            ),
            primaryButtonAction: () => navigate.pathwayReview({ uuid }),
            secondaryButtonText: t(
                'pathways:pathway_session_complete.pathway_complete.action.return',
            ),
            secondaryButtonAction: () => navigate.pathway({ uuid }),
        },
        [SessionCompleteState.SESSION_COMPLETE]: {
            title: t('pathways:pathway_session_complete.session_complete.title', {
                sessionNumber: activeSession,
            }),
            description: (() => {
                if (activeSession === 1) {
                    return t(
                        'pathways:pathway_session_complete.session_complete.first_session.description',
                    );
                }

                if (activeSession + 1 < pathway.sessionCount) {
                    return t(
                        'pathways:pathway_session_complete.session_complete.middle_session.description',
                    );
                }

                return t(
                    'pathways:pathway_session_complete.session_complete.last_session.description',
                );
            })(),
            renderImage: () => (
                <Image
                    src={tada}
                    alt={t('pathways:pathway_session_complete.session_complete.title', {
                        sessionNumber: activeSession,
                    })}
                />
            ),
            primaryButtonText: t(
                'pathways:pathway_session_complete.session_complete.action.continue',
            ),
            primaryButtonAction: () => navigate.pathway({ uuid }),
        },
    };

    const {
        title,
        description,
        renderImage,
        primaryButtonText,
        primaryButtonAction,
        secondaryButtonText,
        secondaryButtonAction,
    } = SessionCompleteStateToProps[completeState];

    return (
        <PageContainer>
            <CloseButton
                title={t('pathways:pathway_session_complete.close')}
                icon="x"
                size="medium"
                variant="secondary"
                onClick={() => navigate.pathway({ uuid })}
            />
            <ContentContainer>
                {renderImage()}
                <HeadingText50 centered>{title}</HeadingText50>
                <BodyTextMedium centered marginBottom={44}>
                    {description}
                </BodyTextMedium>
                <SessionProgress>
                    {sessionProgress.map((state, i) => {
                        const sessionNumber = i + 1;
                        const sessionLabel = t(
                            'pathways:pathway_session_complete.pathway_complete.session_progress_title',
                            {
                                number: sessionNumber,
                            },
                        );
                        const isLastSession = sessionProgress.length === sessionNumber;

                        return (
                            <React.Fragment key={state + i}>
                                <SessionProgressItem>
                                    <RenderSwitch expression={state}>
                                        <RenderSwitch.Case
                                            expression={SessionStatusState.COMPLETED}
                                        >
                                            <IconWrapper
                                                css={{
                                                    backgroundColor: colors.green40,
                                                }}
                                            >
                                                <Icon
                                                    name="checkmark"
                                                    size={SESSION_ICON_SIZE / 1.5}
                                                    color="white"
                                                />
                                            </IconWrapper>
                                            <ComponentTextSmall marginTop={12} fontColor="green55">
                                                {sessionLabel}
                                            </ComponentTextSmall>
                                        </RenderSwitch.Case>
                                        <RenderSwitch.Case expression={SessionStatusState.NEXT}>
                                            <IconWrapper
                                                css={{
                                                    borderColor: colors.blue55,
                                                    borderWidth: 3,
                                                    borderStyle: 'solid',
                                                }}
                                            >
                                                <Icon
                                                    name="star"
                                                    size={SESSION_ICON_SIZE / 2.5}
                                                    color="blue55"
                                                />
                                            </IconWrapper>
                                            <ComponentTextSmall marginTop={12} fontColor="gray85">
                                                {sessionLabel}
                                            </ComponentTextSmall>
                                        </RenderSwitch.Case>
                                        <RenderSwitch.Case expression={SessionStatusState.LOCKED}>
                                            <IconWrapper
                                                css={{
                                                    backgroundColor: colors.gray05,
                                                }}
                                            >
                                                <Icon
                                                    name="lock"
                                                    size={SESSION_ICON_SIZE / 2.25}
                                                    color="gray30"
                                                />
                                            </IconWrapper>
                                            <ComponentTextSmall marginTop={12} fontColor="gray55">
                                                {sessionLabel}
                                            </ComponentTextSmall>
                                        </RenderSwitch.Case>
                                    </RenderSwitch>
                                </SessionProgressItem>
                                {!isLastSession && (
                                    <SessionProgressBar
                                        css={{
                                            backgroundColor:
                                                state === SessionStatusState.COMPLETED
                                                    ? colors.green40
                                                    : colors.gray10,
                                        }}
                                    />
                                )}
                            </React.Fragment>
                        );
                    })}
                </SessionProgress>

                <ButtonContainer>
                    <Button fluid variant="primary" size="large" onClick={primaryButtonAction}>
                        {primaryButtonText}
                    </Button>
                    {secondaryButtonText ? (
                        <Button
                            css={{ marginTop: pxToRem(18), background: 'none' }}
                            fluid
                            variant="flat"
                            size="large"
                            onClick={secondaryButtonAction}
                        >
                            {secondaryButtonText}
                        </Button>
                    ) : null}
                </ButtonContainer>
            </ContentContainer>
            <PathwayCloudBackground />
        </PageContainer>
    );
};

const AVATAR_SIZE = 180;
const SESSION_ICON_SIZE = 50;

const CloseButton = styled(IconButton)({
    position: 'absolute',
    top: pxToRem(24),
    right: pxToRem(24),
});

const PageContainer = styled.div({
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    justifyContent: 'center',
    alignContent: 'center',
    minHeight: '100vh',
});

const Image = styled.img({
    width: '60%',
    alignSelf: 'center',
    marginBottom: pxToRem(24),
});

const ConfettiContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: pxToRem(220),
});

const SuccessAnimation = styled(Lottie)({
    width: pxToRem(400),
    maxHeight: pxToRem(250),
    opacity: 0.7,
    position: 'absolute',
    overflow: 'hidden',
    left: 'calc(50% - 200px)',
});

const ProviderAvatar = styled.img({
    display: 'block',
    width: pxToRem(AVATAR_SIZE),
    height: pxToRem(AVATAR_SIZE),
    margin: '0 auto',
    border: 'none',
    borderRadius: '50%',
    objectFit: 'cover',
    objectPosition: 'center',
    marginBottom: pxToRem(32),
});

const ContentContainer = styled(PageContentContainer)({
    display: 'flex',
    flexDirection: 'column',
    maxWidth: pxToRem(560),
});

const ButtonContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'center',
    flexDirection: 'column',
    minWidth: pxToRem(270),
});

const SessionProgress = styled.div({
    display: 'flex',
    marginLeft: 30,
    marginRight: 30,
    marginBottom: 80,
    justifyContent: 'space-between',
});

const SessionProgressItem = styled.div({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
});

const SessionProgressBar = styled.div({
    height: 3,
    flex: 0.23,
    marginTop: SESSION_ICON_SIZE / 2,
});

const IconWrapper = styled.div({
    height: SESSION_ICON_SIZE,
    width: SESSION_ICON_SIZE,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});
